import Modal from '../modal/modal'
import { bodyElement } from '../common/body-element'
import { type Nullable } from '../../types/common'
import { PopupStorage } from './popup-storage'
import { PopupType, type PopupVariant } from './types'
import { ConditionRuntime } from './condition-runtime'
import { POPUP_ANIMATION_DELAY, gradientOpacify } from './utils'
import { POPUP_CLASSNAMES } from './constants'
import { checkMobileVersion } from '../../utils/check-media'

export class PopupSignup extends Modal {
  storage: PopupStorage
  conditionRuntime?: ConditionRuntime
  variant: PopupVariant
  hasImage: boolean;
  hideImgOnMobile: boolean;

  constructor() {
    super()
    this.storage = new PopupStorage(this.dataset.blockId ?? this.id)
    this.variant = this.dataset.variant as PopupVariant
    this.hasImage = this.dataset.hasImage === 'true'
    this.hideImgOnMobile = this.dataset.hideImgMobile === 'true'
  }

  connectedCallback(): void {
    if (this.isEditorMode) {
      this.editor.subscribe('BLOCK_SELECT', this._handleBlockSelect)
      this.editor.subscribe('BLOCK_DESELECT', this._handleBlockDeselect)
      return
    }

    const data = this.storage.loadData()

    // don't show on captcha page
    if (window.location.pathname.includes('challenge')) {
      return
    }

    // handle success message
    if (
      this.dataset.showSuccessMessage === 'true' &&
      window.location.search.includes('customer_posted') &&
      window.sessionStorage.getItem('customer-posted-block-id') ===
        this.dataset.blockId
    ) {
      this.show()
      window.sessionStorage.removeItem('customer-posted-block-id')
      this.classList.add(POPUP_CLASSNAMES.success)
      return
    }

    if (!data || data.type === PopupType.Signup) {
      if (data?.lastShow) {
        try {
          data.lastShow = new Date(data.lastShow)
        } catch {
          data.lastShow = null
        }
      }

      this.conditionRuntime = new ConditionRuntime(
        this.dataset,
        data?.lastShow ?? null,
        this.show,
      )
      this.conditionRuntime.run()
    }
  }

  disconnectedCallback(): void {
    if (this.isEditorMode) {
      this.editor.destroy()
    }
    this.conditionRuntime?.cleanup()
  }

  _handleBlockSelect = ({ detail: { blockId } }): void => {
    if (this.dataset.blockId === blockId) {
      this.show()
    }
  }

  _handleBlockDeselect = ({ detail: { blockId } }): void => {
    if (this.dataset.blockId === blockId) {
      this.hide()
    }
  }

  show = async (): Promise<void> => {
    if (this.variant === 'lightbox') {
      const withGradient = !!this.dataset.overlayGradient

      const background = withGradient
        ? gradientOpacify(
            this.dataset.overlayGradient,
            this.dataset.overlayOpacity,
          )
        : `color-mix(in srgb, ${this.dataset.overlaySolid} ${
            this.dataset.overlayOpacity * 100
          }%, transparent)`

      bodyElement.showOverlay(this.id, {
        zIndex: 15,
        background,
        backdropFilter: `blur(${this.dataset.overlayBlur}px)`,
        transitionDuration: `${POPUP_ANIMATION_DELAY}ms`,
      })
    }

    await super.show()

    if (this.variant === 'lightbox') {
      this.classList.add(POPUP_CLASSNAMES.lightboxActive)
    }

    if (this.hasImage) {
      if (this.hideImgOnMobile && checkMobileVersion()) {
        return
      }
      
      setTimeout(() => {
        this.classList.add(POPUP_CLASSNAMES.showImage)
      }, POPUP_ANIMATION_DELAY)
    }

    const closeBtn = this.querySelector(".popup__close-button") as HTMLElement;
    if (closeBtn) {
      closeBtn.style.display = "flex"
    }

    this.selectBtn('close')?.addEventListener('click', this.hide)
    this.selectBtn('close-small')?.addEventListener('click', this.hide)
    this.selectBtn('submit')?.addEventListener('click', this.onTouch)
  }

  hide = async (): Promise<void> => {
    this.storage.saveData({
      type: PopupType.Signup,
      lastShow: new Date(),
    })

    if (this.variant === 'lightbox') {
      bodyElement.hideOverlay(this.id)
    }

    this.conditionRuntime?.cleanup()

    this.selectBtn('close')?.removeEventListener('click', this.hide)
    this.selectBtn('close-small')?.removeEventListener('click', this.hide)
    this.selectBtn('submit')?.removeEventListener('click', this.onTouch)

    if (this.variant === 'lightbox') {
      this.classList.remove(POPUP_CLASSNAMES.lightboxActive)
      this.classList.add(POPUP_CLASSNAMES.lightboxClosing)
    }

    await super.hide()
    this.classList.remove(POPUP_CLASSNAMES.showImage)
    this.classList.remove(POPUP_CLASSNAMES.lightboxClosing)
  }

  onTouch = (): void => {
    // for success page detection
    window.sessionStorage.setItem(
      'customer-posted-block-id',
      this.dataset.blockId,
    )
  }

  selectBtn(type: 'submit' | 'close' | 'close-small'): Nullable<HTMLElement> {
    if (!this.isOpen) {
      return null
    }

    return this.querySelector(`[data-${type}-btn]`)
  }
}
